import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  cloneDeep,
  filter,
  find,
  get,
  gt,
  includes,
  map,
  toLower,
  isEqual,
} from 'lodash';

import { FormControl } from '@angular/forms';
import { BehaviorSubject, Subscription } from '@shared/rx';
import { ComponentStaticText, Primitive } from '@shared/types';
import { getObservableValue } from '@app/shared/utils';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

@Component({
  selector: 'xux-switcher',
  templateUrl: './xux-switcher.component.html',
  styleUrls: ['./xux-switcher.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxSwitcherComponent<
    T extends { selected: boolean; value: string } = {
      selected: boolean;
      value: string;
      [key: string]: Primitive | object;
    },
  >
  implements OnInit, OnDestroy
{
  @Input() public params: {
    selectOptions?: T[];
    selectedId?: string;
    addItemCaption?: string;

    containerClass?: string;

    isDisabled$?: BehaviorSubject<boolean>;
    hidden?: boolean;
    isAddButtonDisabled?: boolean;
  } = {};
  @Output() public optionChanged: EventEmitter<T> = new EventEmitter();
  @Output() public addNewItemClick: EventEmitter<void> = new EventEmitter();

  public isAddButtonDisabled: boolean = false;
  public allSelectOption: T[] = [];
  public selectedId$: BehaviorSubject<string> = new BehaviorSubject('');
  public isDisabled$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public selectOptions$: BehaviorSubject<T[]> = new BehaviorSubject([]);
  public isDataSourceEmpty$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public searchControl: FormControl = new FormControl('');
  public subscriptions: Subscription[] = [];
  public placeholder: string = this.i18nService.translate(I18n.Common.Search);
  public hidden: boolean = false;
  public addItemCaption: string = '';
  public staticTextContent: ComponentStaticText = {
    toggleDropdown: I18n.Common.ToggleDropdown,
    SwitchSite: I18n.Common.SwitchSite,
    NoMatches: I18n.Common.NoMatches,
  };

  public constructor(private i18nService: XVII18nService) {}

  public ngOnInit(): void {
    this.allSelectOption = cloneDeep(this.params.selectOptions);
    this.selectedId$.next(this.params.selectedId);
    this.isDisabled$ = get(this.params, 'isDisabled$', this.isDisabled$);
    this.isAddButtonDisabled = get(
      this.params,
      'isAddButtonDisabled',
      this.isAddButtonDisabled,
    );
    this.hidden = get(this.params, 'hidden', this.hidden);

    this.addItemCaption = this.params.addItemCaption ?? this.addItemCaption;
    this.subscriptions.push(
      this.selectedId$.subscribe((id) => {
        this.selectOptions$.next(
          map(this.allSelectOption, (option) => {
            option.selected = option.value === id;

            return option;
          }),
        );
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe(),
    );
  }

  public selectOption(id: string): void {
    if (isEqual(id, getObservableValue(this.selectedId$))) {
      return;
    }

    this.selectedId$.next(id);
    const selectedOption = find(
      this.allSelectOption,
      (option) => option.value === id,
    );
    this.optionChanged.emit(selectedOption);
  }

  public onFilter(value: string): void {
    const matchedSelectOptions = filter(this.allSelectOption, (item: any) =>
      includes(toLower(item.text), toLower(value)),
    );
    this.selectOptions$.next(matchedSelectOptions);
    this.isDataSourceEmpty$.next(!gt(matchedSelectOptions.length, 0));
  }

  public clickSearchBox($event): void {
    $event.stopPropagation();
  }

  public addNewItem(): void {
    this.addNewItemClick.emit();
  }
}
